/**
 * WoSampleReconcileDialog — step 2 of the work-order sample upload (2026-09-10):
 * the server's dry-run plan, per test, before anything is written.
 *
 *   - PLACEHOLDER lines ("Samples pending receipt", from a quote conversion)
 *     are REPLACED by the file's samples; extra instances become new lines;
 *     rows already on the work order are skipped.
 *   - Per test the resulting count is compared with what the schedule was
 *     sized for (the quote quantity, else the lines on the WO). Any mismatch —
 *     more, fewer, or a test that was never quoted — is a schedule impact: the
 *     user must tick the acknowledgement to import (the schedule then shows
 *     "Out of sync" until Sync now), or cancel and nothing is imported.
 *   - "More than expected" / unquoted groups expand into a per-instance picker
 *     so the user can leave surplus rows out instead (Vahid's mapping ask).
 */
import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import type {
  GroupOutcome, ReconcileGroup, ReconcileInstance, WoSampleUploadReconcileResult,
} from '../../types/workOrderSampleUpload';

interface Props {
  open: boolean;
  plan: WoSampleUploadReconcileResult | null;
  /** True while a reconcile round-trip (after an exclusion toggle) is in flight. */
  refreshing: boolean;
  applying: boolean;
  error: string | null;
  excludedInstanceIds: ReadonlySet<string>;
  onToggleInstance: (instanceId: string, included: boolean) => void;
  onConfirm: (opts: { dropUnfilledPlaceholders: boolean; acknowledgeScheduleImpact: boolean }) => void;
  onCancel: () => void;
}

const OUTCOME: Record<GroupOutcome, { label: (g: ReconcileGroup) => string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  match: { label: (g) => (g.resultingCount === 0 && g.placeholderCount + g.existingRealCount === 0 ? 'Left out' : 'Matches'), color: 'success' },
  more: { label: (g) => `${g.surplus} more than expected`, color: 'warning' },
  fewer: { label: (g) => `${g.shortfall} fewer than expected`, color: 'warning' },
  unquoted: { label: () => 'Not on this work order', color: 'error' },
};

const ACTION_LABEL: Record<ReconcileInstance['action'], string> = {
  'fill-placeholder': 'Replaces a placeholder row',
  create: 'New test line',
  'already-present': 'Already on the work order',
};

const fmtDepth = (top: number | null, base: number | null): string =>
  top == null && base == null ? '—' : `${top ?? '?'}${base != null ? ` – ${base}` : ''} m`;

export const WoSampleReconcileDialog = ({
  open, plan, refreshing, applying, error, excludedInstanceIds, onToggleInstance, onConfirm, onCancel,
}: Props) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dropUnfilled, setDropUnfilled] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const unfilledCount = useMemo(
    () => plan?.groups.reduce((n, g) => n + g.unfilledPlaceholderKeys.length, 0) ?? 0,
    [plan],
  );
  const importable = (plan?.totals.fill ?? 0) + (plan?.totals.create ?? 0);
  const nothingToDo = importable === 0 && !(dropUnfilled && unfilledCount > 0);
  const needsAck = Boolean(plan?.scheduleImpact) && !acknowledged;
  const busy = refreshing || applying;

  const handleClose = () => { setExpanded({}); setDropUnfilled(false); setAcknowledged(false); onCancel(); };

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          Match uploaded samples to {plan?.workOrderId ?? 'the work order'}
          {refreshing && <CircularProgress size={16} />}
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={busy}><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {!plan ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Nothing is saved until you confirm. Placeholder rows (&ldquo;Samples pending receipt&rdquo;) are
              replaced by the uploaded samples; rows already on the work order are skipped.
              {plan.hasQuoteExpectations
                ? ' "Expected" is the quantity on the quote this work order came from — the number its schedule was sized for.'
                : ' "Expected" is the number of lines currently on the work order for each test.'}
            </Typography>

            {/* Totals */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" color="success" variant="outlined" label={`${plan.totals.fill} placeholder${plan.totals.fill === 1 ? '' : 's'} replaced`} />
              <Chip size="small" color="primary" variant="outlined" label={`${plan.totals.create} new test line${plan.totals.create === 1 ? '' : 's'}`} />
              {plan.totals.alreadyPresent > 0 && (
                <Chip size="small" variant="outlined" label={`${plan.totals.alreadyPresent} already on the work order`} />
              )}
              {plan.totals.excluded > 0 && (
                <Chip size="small" color="default" variant="outlined" label={`${plan.totals.excluded} left out`} />
              )}
            </Box>

            {/* Per-test table */}
            <TableContainer sx={{ border: `1px solid ${alpha(theme.border.color, 0.5)}`, borderRadius: 1, maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: theme.table.headerBg, fontWeight: 600, fontSize: '0.75rem' } }}>
                    <TableCell sx={{ width: 36 }} />
                    <TableCell>Test</TableCell>
                    <TableCell align="right">On work order</TableCell>
                    <TableCell align="right">Expected</TableCell>
                    <TableCell align="right">In file</TableCell>
                    <TableCell align="right">After import</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.groups.map((g) => {
                    const o = OUTCOME[g.outcome];
                    const canPick = g.instances.some((i) => i.action !== 'already-present');
                    const isOpen = expanded[g.testKey] ?? (g.outcome === 'more' || g.outcome === 'unquoted');
                    return (
                      <GroupRows
                        key={g.testKey} group={g} outcomeLabel={o.label(g)} outcomeColor={o.color}
                        expandable={canPick} expanded={isOpen}
                        onToggleExpand={() => setExpanded((p) => ({ ...p, [g.testKey]: !isOpen }))}
                        excludedInstanceIds={excludedInstanceIds} onToggleInstance={onToggleInstance} disabled={busy}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {plan.unresolvedTests.length > 0 && (
              <Alert severity="error">
                These tests could not be matched to a lab test and will be skipped:{' '}
                {plan.unresolvedTests.map((u) => `${u.testName} (row${u.rows.length === 1 ? '' : 's'} ${u.rows.map((r) => r + 1).join(', ')})`).join('; ')}.
              </Alert>
            )}

            {unfilledCount > 0 && (
              <FormControlLabel
                control={<Checkbox size="small" checked={dropUnfilled} onChange={(e) => setDropUnfilled(e.target.checked)} disabled={busy} />}
                label={`Remove the ${unfilledCount} placeholder row${unfilledCount === 1 ? '' : 's'} this file does not fill (frees their future schedule days)`}
              />
            )}

            {plan.scheduleImpact && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  This import changes what the schedule was sized for.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, mb: 1 }}>
                  {plan.warnings.map((w, i) => <li key={i}><Typography variant="body2">{w}</Typography></li>)}
                </Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Untick the extra rows above to import only what was expected — or continue: the work order&rsquo;s
                  schedule will show <b>Out of sync</b> until you open it and run <b>Sync now</b>.
                </Typography>
                <FormControlLabel
                  control={<Checkbox size="small" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} disabled={busy} />}
                  label="I understand — import anyway and mark the schedule out of sync"
                />
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel — import nothing</Button>
        <Tooltip title={needsAck ? 'Tick the acknowledgement above, or untick the extra rows.' : nothingToDo ? 'Every uploaded row is already on this work order.' : ''}>
          <span>
            <Button
              variant="contained" color={plan?.scheduleImpact ? 'warning' : 'primary'}
              disabled={!plan || busy || needsAck || nothingToDo}
              onClick={() => onConfirm({ dropUnfilledPlaceholders: dropUnfilled, acknowledgeScheduleImpact: acknowledged })}
              sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
            >
              {applying ? 'Importing…' : plan?.scheduleImpact
                ? `Import ${importable} & flag schedule`
                : `Import ${importable} test line${importable === 1 ? '' : 's'}`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

interface GroupRowsProps {
  group: ReconcileGroup;
  outcomeLabel: string;
  outcomeColor: 'success' | 'warning' | 'error' | 'info';
  expandable: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  excludedInstanceIds: ReadonlySet<string>;
  onToggleInstance: (instanceId: string, included: boolean) => void;
  disabled: boolean;
}

/** One test group: the summary row plus its collapsible per-instance picker. */
const GroupRows = ({
  group: g, outcomeLabel, outcomeColor, expandable, expanded, onToggleExpand,
  excludedInstanceIds, onToggleInstance, disabled,
}: GroupRowsProps) => {
  const theme = useTheme();
  const onWo = g.placeholderCount + g.existingRealCount;
  return (
    <>
      <TableRow hover sx={g.outcome !== 'match' ? { backgroundColor: alpha(theme.palette[outcomeColor].main, 0.05) } : undefined}>
        <TableCell padding="none" sx={{ pl: 0.5 }}>
          {expandable && (
            <IconButton size="small" onClick={onToggleExpand} aria-label={expanded ? 'Hide rows' : 'Show rows'}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{g.testName}</TableCell>
        <TableCell align="right">
          {onWo}
          {g.placeholderCount > 0 && (
            <Typography component="span" variant="caption" color="text.secondary"> ({g.placeholderCount} placeholder{g.placeholderCount === 1 ? '' : 's'})</Typography>
          )}
        </TableCell>
        <TableCell align="right">{g.expected ?? '—'}</TableCell>
        <TableCell align="right">
          {g.incomingCount}
          {g.alreadyPresent > 0 && (
            <Typography component="span" variant="caption" color="text.secondary"> (+{g.alreadyPresent} already there)</Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>{g.resultingCount}</TableCell>
        <TableCell><Chip size="small" color={outcomeColor} variant="outlined" label={outcomeLabel} sx={{ fontSize: '0.7rem', height: 22 }} /></TableCell>
      </TableRow>
      {expandable && (
        <TableRow>
          <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
            <Collapse in={expanded} unmountOnExit>
              <Box sx={{ px: 2, py: 1, backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Untick a row to leave it out of this import.
                </Typography>
                <Table size="small">
                  <TableBody>
                    {g.instances.map((inst) => {
                      const present = inst.action === 'already-present';
                      const included = present ? false : !excludedInstanceIds.has(inst.instanceId);
                      return (
                        <TableRow key={inst.instanceId} sx={{ opacity: present || !included ? 0.5 : 1, '& td': { border: 0, py: 0.25 } }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={included} disabled={present || disabled}
                              onChange={(e) => onToggleInstance(inst.instanceId, e.target.checked)} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{inst.locationId}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{inst.sampleRef ?? '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{fmtDepth(inst.topDepth, inst.baseDepth)}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {present ? ACTION_LABEL[inst.action] : included ? ACTION_LABEL[inst.action] : 'Left out'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
